import { useLocale } from '../use-locale'

/**
 * Hook to format numbers according to the current locale
 */
export function useFormatNumber() {
  const locale = useLocale()

  /**
   * Formats a number with thousands separators according to the locale
   * @param value - The number to format
   * @param options - Intl.NumberFormat options
   * @returns Formatted number string
   */
  const formatNumber = (
    value: number,
    options?: Intl.NumberFormatOptions,
  ): string => {
    return new Intl.NumberFormat(locale, options).format(value)
  }

  /**
   * Formats a number as currency according to the locale
   * @param value - The number to format
   * @param currency - The currency code (e.g., 'BRL', 'USD')
   * @param options - Additional Intl.NumberFormat options
   * @returns Formatted currency string
   */
  const formatCurrency = (
    value: number,
    currency = 'BRL',
    options?: Intl.NumberFormatOptions,
  ): string => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      ...options,
    }).format(value)
  }

  /**
   * Formats a number as a percentage according to the locale
   * @param value - The number to format (0.5 = 50%)
   * @param options - Additional Intl.NumberFormat options
   * @returns Formatted percentage string
   */
  const formatPercent = (
    value: number,
    options?: Intl.NumberFormatOptions,
  ): string => {
    return new Intl.NumberFormat(locale, {
      style: 'percent',
      ...options,
    }).format(value)
  }

  /**
   * Formats a number with a specific number of decimal places
   * @param value - The number to format
   * @param decimals - Number of decimal places
   * @returns Formatted number string
   */
  const formatDecimal = (value: number, decimals = 2): string => {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value)
  }

  /**
   * Formats a number in compact notation (e.g., 1K, 1M, 1B)
   * @param value - The number to format
   * @param options - Additional Intl.NumberFormat options
   * @returns Formatted compact number string
   */
  const formatCompact = (
    value: number,
    options?: Intl.NumberFormatOptions,
  ): string => {
    return new Intl.NumberFormat(locale, {
      notation: 'compact',
      compactDisplay: 'short',
      ...options,
    }).format(value)
  }

  return {
    formatNumber,
    formatCurrency,
    formatPercent,
    formatDecimal,
    formatCompact,
    locale,
  }
}
